import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Spin,
  Alert,
  Tag,
  Row,
  Col,
} from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { createTask, getAssignableUsers, getAssignableGroups } from '../lib/api';
import { getUserDisplayName } from '../lib/user';
import RichTextEditor from '../components/RichTextEditor';

const { Title, Text } = Typography;
const { Option } = Select;

interface User {
  id: number;
  name?: string;
  username?: string;
  email?: string;
  role: string;
}

interface Group {
  id: number;
  name: string;
  description?: string;
  members: { id: number; name?: string; username?: string; email?: string }[];
}

export default function NewTask() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersData, groupsData] = await Promise.all([
          getAssignableUsers(),
          getAssignableGroups(),
        ]);
        setUsers(usersData);
        setGroups(groupsData);
      } catch (err: any) {
        setError(err.message || '無法取得資料');
      } finally {
        setFetchLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      await createTask({
        title: values.title,
        description: values.description,
        priority: values.priority,
        dueDate: values.dueDate?.format('YYYY-MM-DD'),
        assigneeIds: values.assigneeIds || [],
        groupIds: values.groupIds || [],
      });
      navigate('/tasks');
    } catch (err: any) {
      setError(err.message || '建立任務失敗');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Link to="/tasks">
        <Button icon={<ArrowLeftOutlined />}>返回任務列表</Button>
      </Link>

      <Card>
        <Title level={2}>指派新任務</Title>

        {error && (
          <Alert title={error} type="error" showIcon style={{ marginBottom: 16 }} />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{ priority: 'medium' }}
        >
          <Form.Item
            name="title"
            label="任務名稱"
            rules={[{ required: true, message: '請輸入任務名稱' }]}
          >
            <Input placeholder="輸入任務名稱" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="priority" label="優先級">
                <Select>
                  <Option value="high">高</Option>
                  <Option value="medium">中</Option>
                  <Option value="low">低</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="dueDate" label="截止日期">
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="指派對象" required>
            <Text type="secondary">選擇個別員工或群組（可複選）</Text>

            {groups.length > 0 && (
              <Form.Item name="groupIds" style={{ marginTop: 8, marginBottom: 8 }}>
                <Select
                  mode="multiple"
                  placeholder="選擇群組"
                  style={{ width: '100%' }}
                  optionLabelProp="label"
                >
                  {groups.map((group) => (
                    <Option key={group.id} value={group.id} label={group.name}>
                      <div>
                        <div>{group.name}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {group.description || `${group.members.length} 位成員`}
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            )}

            <Form.Item name="assigneeIds" style={{ marginBottom: 0 }}>
              <Select
                mode="multiple"
                placeholder="選擇個別員工"
                style={{ width: '100%' }}
                optionLabelProp="label"
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id} label={getUserDisplayName(user)}>
                    {getUserDisplayName(user)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Form.Item>

          {/* Preview of selected assignees */}
          <Form.Item shouldUpdate={(prevValues, currentValues) =>
            prevValues.assigneeIds !== currentValues.assigneeIds ||
            prevValues.groupIds !== currentValues.groupIds
          }>
            {({ getFieldValue }) => {
              const selectedAssignees = getFieldValue('assigneeIds') || [];
              const selectedGroups = getFieldValue('groupIds') || [];

              // Calculate expanded assignees
              const assigneeIds = new Set<number>(selectedAssignees);
              selectedGroups.forEach((groupId: number) => {
                const group = groups.find((g) => g.id === groupId);
                group?.members.forEach((m) => assigneeIds.add(m.id));
              });
              const expandedAssignees = Array.from(assigneeIds)
                .map((id) => users.find((u) => u.id === id))
                .filter(Boolean) as User[];

              if (selectedAssignees.length === 0 && selectedGroups.length === 0) {
                return null;
              }

              return (
                <Card size="small" title="已選擇的指派對象" style={{ background: '#f6ffed', marginBottom: 24 }}>
                  <Space size="small" wrap>
                    {selectedGroups.map((groupId: number) => {
                      const group = groups.find((g) => g.id === groupId);
                      return group ? (
                        <Tag key={groupId} color="purple">
                          群組: {group.name}
                        </Tag>
                      ) : null;
                    })}
                    {selectedAssignees.map((userId: number) => {
                      const user = users.find((u) => u.id === userId);
                      return user ? (
                        <Tag key={userId} color="blue">
                          {getUserDisplayName(user)}
                        </Tag>
                      ) : null;
                    })}
                  </Space>
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary">
                      共 {expandedAssignees.length} 人將收到此任務
                      {expandedAssignees.length > 0 && (
                        <span style={{ marginLeft: 8 }}>
                          ({expandedAssignees.map((u) => getUserDisplayName(u)).join('、')})
                        </span>
                      )}
                    </Text>
                  </div>
                </Card>
              );
            }}
          </Form.Item>

          <Form.Item name="description" label="任務描述">
            <RichTextEditor
              placeholder="輸入任務詳細描述"
              height={200}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Link to="/tasks">
                <Button>取消</Button>
              </Link>
              <Form.Item shouldUpdate={(prevValues, currentValues) =>
                prevValues.assigneeIds !== currentValues.assigneeIds ||
                prevValues.groupIds !== currentValues.groupIds
              } noStyle>
                {({ getFieldValue }) => {
                  const selectedAssignees = getFieldValue('assigneeIds') || [];
                  const selectedGroups = getFieldValue('groupIds') || [];
                  const hasAssignees = selectedAssignees.length + selectedGroups.length > 0;
                  return (
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<PlusOutlined />}
                      loading={loading}
                      disabled={!hasAssignees}
                    >
                      建立任務
                    </Button>
                  );
                }}
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </Space>
  );
}
